/**
 * The local backstop for unsent work.
 *
 * sessionStorage, NEVER localStorage, and the reason is specific to who uses this. Field
 * staff complete these packets on shared and family phones. localStorage would write a
 * named person's answers onto that phone with no expiry and no owner, surviving until
 * someone clears site data — on a device the agency does not control and cannot wipe.
 * sessionStorage dies with the tab.
 *
 * The server copy is authoritative. This exists for the case the server never heard: the
 * tab was backgrounded on LTE and mobile Safari discarded it without firing `beforeunload`.
 */

const PREFIX = 'hr2.portal.draft.';

export interface LocalDraft {
  readonly value: unknown;
  readonly revision: number;
}

/** Every access is wrapped: a private window or blocked site data throws on access. */
export function readLocalDraft(instanceId: string): LocalDraft | null {
  try {
    const raw = sessionStorage.getItem(PREFIX + instanceId);
    if (raw === null) return null;
    const parsed: unknown = JSON.parse(raw);
    if (typeof parsed !== 'object' || parsed === null) return null;
    const revision = (parsed as { revision?: unknown }).revision;
    if (typeof revision !== 'number') return null;
    return { value: (parsed as { value?: unknown }).value, revision };
  } catch {
    return null;
  }
}

export function writeLocalDraft(instanceId: string, draft: LocalDraft): void {
  try {
    sessionStorage.setItem(PREFIX + instanceId, JSON.stringify(draft));
  } catch {
    // Full, blocked, or a private window. The server copy is the real one, so losing this
    // is a degraded backstop rather than lost work.
  }
}

export function clearLocalDraft(instanceId: string): void {
  try {
    sessionStorage.removeItem(PREFIX + instanceId);
  } catch {
    /* nothing to do */
  }
}

/**
 * Whichever copy is newer wins, and the caller is told which — so the person can be shown
 * "we restored what you had" rather than silently seeing older text reappear.
 */
export function reconcile(
  server: { readonly value: unknown; readonly revision: number } | null,
  local: LocalDraft | null,
): { readonly value: unknown; readonly revision: number; readonly from: 'server' | 'device' | 'none' } {
  if (local !== null && (server === null || local.revision > server.revision)) {
    return { value: local.value, revision: local.revision, from: 'device' };
  }
  if (server !== null) return { value: server.value, revision: server.revision, from: 'server' };
  return { value: null, revision: -1, from: 'none' };
}
